package io.jsonize.web.screens.editor

import androidx.compose.runtime.*
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.viewinterop.WebElementView
import kotlinx.browser.document
import org.w3c.dom.DragEvent
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.get
import org.w3c.files.File
import io.jsonize.web.compare.LineDiff
import io.jsonize.web.compare.LineDiffKind
import kotlin.math.max

/*Need to add show only differences in Compare View
Highlight indicator issue not placing correctly
Compare -> Swap, Clear, Show Only changes, Highlight changed line in very lite color
Toolbar*/

@OptIn(ExperimentalComposeUiApi::class)
@Composable
fun EditorTextArea(
    text: String,
    onTextChange: (String) -> Unit,
    onDropFile: (File) -> Unit = {},
    onDropText: (String) -> Unit = {},
    lineDiffs: List<LineDiff> = emptyList(),
    errorLine: Int? = null,
    isStreaming: Boolean,
    isDark: Boolean = false,
    placeholderText: String = "",
    modifier: Modifier = Modifier,
    jumpToOffset: Int? = null,
    onEditorReady: (EditorView) -> Unit = {},
    onEditorDispose: () -> Unit = {}
) {
    val editorRef = remember { mutableStateOf<EditorView?>(null) }
    val markerLayerRef = remember { mutableStateOf<HTMLElement?>(null) }
    val pushingFromCompose = remember { mutableStateOf(false) }
    val editableCompartment = remember { Compartment() }
    val themeCompartment = remember { Compartment() }
    val diffCompartment = remember { Compartment() }
    val onDropFileState by rememberUpdatedState(onDropFile)
    val onDropTextState by rememberUpdatedState(onDropText)
    val onTextChangeState = rememberUpdatedState(onTextChange)

    DisposableEffect(editorRef.value) {
        val view = editorRef.value ?: return@DisposableEffect onDispose { }
        val root = view.state as? HTMLElement ?: return@DisposableEffect onDispose { }
        val targets = linkedSetOf<HTMLElement>()
        targets.add(root)
        (root.parentElement as? HTMLElement)?.let { targets.add(it) }
        (view.asDynamic().contentDOM as? HTMLElement)?.let { targets.add(it) }
        (view.asDynamic().scrollDOM as? HTMLElement)?.let { targets.add(it) }

        val dragEnterListener = EventListener { event ->
            val dragEvent = event as? DragEvent ?: return@EventListener
            dragEvent.preventDefault()
            dragEvent.stopImmediatePropagation()
        }

        val dragOverListener = EventListener { event ->
            val dragEvent = event as? DragEvent ?: return@EventListener
            dragEvent.preventDefault()
            dragEvent.stopImmediatePropagation()
        }

        val dropListener = EventListener { event ->
            val dragEvent = event as? DragEvent ?: return@EventListener
            dragEvent.preventDefault()
            dragEvent.stopImmediatePropagation()

            val dt = dragEvent.dataTransfer ?: return@EventListener

            if (dt.files.length > 0) {
                for (i in 0 until dt.files.length) {
                    val file = dt.files.item(i) ?: continue
                    val name = file.name.lowercase()
                    if (name.endsWith(".json") || name.endsWith(".txt")) {
                        onDropFileState(file)
                        return@EventListener
                    }
                }
            }

            if (dt.items.length > 0) {
                for (i in 0 until dt.items.length) {
                    val item = dt.items[i] ?: continue
                    if (item.kind == "string") {
                        item.getAsString { dropped ->
                            if (!dropped.isNullOrBlank()) {
                                onDropTextState(dropped)
                            }
                        }
                        return@EventListener
                    }
                }
            }

            val plainText = dt.getData("text/plain")
            if (plainText.isNotBlank()) {
                onDropTextState(plainText)
            }
        }

        targets.forEach { target ->
            target.addEventListener("dragenter", dragEnterListener, true)
            target.addEventListener("dragover", dragOverListener, true)
            target.addEventListener("drop", dropListener, true)
        }

        onDispose {
            targets.forEach { target ->
                target.removeEventListener("dragenter", dragEnterListener, true)
                target.removeEventListener("dragover", dragOverListener, true)
                target.removeEventListener("drop", dropListener, true)
            }
        }
    }

    WebElementView(
        factory = {
            val div = document.createElement("div") as HTMLDivElement
            div.style.width = "100%"
            div.style.height = "100%"
            div.style.background = "transparent"
            div.style.overflowY = "hidden"
            div.style.overflowX = "hidden"
            div.style.borderRight = if (isDark) {
                "1px solid rgba(255,255,255,0.08)"
            } else {
                "1px solid rgba(0,0,0,0.08)"
            }
            div.style.position = "relative"
            val markerLayer = document.createElement("div") as HTMLDivElement
            markerLayer.style.position = "absolute"
            markerLayer.style.top = "0"
            markerLayer.style.right = "2px"
            markerLayer.style.height = "100%"
            markerLayer.style.width = "6px"
//            markerLayer.style.pointerEvents = "none"
            markerLayer.style.zIndex = "5"
            div.appendChild(markerLayer)
            markerLayerRef.value = markerLayer
            div
        },
        update = { el ->
            el.style.background = "transparent"
            el.style.borderRight = if (isDark) {
                "1px solid rgba(255,255,255,0.08)"
            } else {
                "1px solid rgba(0,0,0,0.08)"
            }
            if (markerLayerRef.value == null) {
                val markerLayer = document.createElement("div") as HTMLDivElement
                markerLayer.style.position = "absolute"
                markerLayer.style.top = "0"
                markerLayer.style.right = "2px"
                markerLayer.style.height = "100%"
                markerLayer.style.width = "6px"
//                markerLayer.style.pointerEvents = "none"
                markerLayer.style.zIndex = "5"
                el.appendChild(markerLayer)
                markerLayerRef.value = markerLayer
            }
            if (editorRef.value == null) {
                val extensions = js("([])")
                extensions.push(lineNumbers())
                extensions.push(EditorView.lineWrapping)
                extensions.push(highlightActiveLineGutter())
                extensions.push(highlightActiveLine())
                extensions.push(foldGutter())
                extensions.push(indentOnInput())
                extensions.push(history())
                extensions.push(search())
                extensions.push(lintGutter())
                extensions.push(linter(jsonParseLinter()))
                extensions.push(json())
                if (placeholderText.isNotBlank()) {
                    extensions.push(placeholder(placeholderText))
                }
                extensions.push(
                    themeCompartment.of(if (isDark) materialDark else materialLight)
                )
                extensions.push(
                    diffCompartment.of(EditorView.decorations.of(Decoration.none))
                )

                val keymaps = js("([])")
                js("Array.prototype.push.apply")(keymaps, defaultKeymap)
                js("Array.prototype.push.apply")(keymaps, historyKeymap)
                js("Array.prototype.push.apply")(keymaps, searchKeymap)
                js("Array.prototype.push.apply")(keymaps, foldKeymap)
                js("Array.prototype.push.apply")(keymaps, lintKeymap)
                keymaps.push(indentWithTab)
                extensions.push(keymap.of(keymaps))

                extensions.push(
                    editableCompartment.of(EditorView.editable.of(!isStreaming))
                )

                val listenerFn: (dynamic) -> Unit = { update ->
                    if (update.docChanged as Boolean) {
                        if (!pushingFromCompose.value) {
                            onTextChangeState.value(update.state.doc.toString())
                        }
                    }
                }
                extensions.push(EditorView.updateListener.of(listenerFn))

                val stateCfg = js("({})")
                stateCfg.doc = text
                stateCfg.extensions = extensions

                val state = EditorState.create(stateCfg)

                val viewCfg = js("({})")
                viewCfg.state = state
                viewCfg.parent = el

                val view = EditorView(viewCfg)
                view.asDynamic().dom.style.height = "100%"
                view.asDynamic().scrollDOM.style.height = "100%"
                view.asDynamic().scrollDOM.style.overflow = "auto"
                view.asDynamic().scrollDOM.style.overflowX = "hidden"
                editorRef.value = view
                onEditorReady(view)
            }
        },
        onReset = {
            editorRef.value?.destroy()
            editorRef.value = null
            markerLayerRef.value = null
            onEditorDispose()
        },
        modifier = modifier.fillMaxSize()
    )

    LaunchedEffect(text, lineDiffs, isDark, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val current = view.state.doc.toString()
        if (current != text) {
            pushingFromCompose.value = true
            view.dispatch(
                js(
                    "({ changes: { from: 0, to: view.state.doc.length, insert: text } })"
                )
            )
            pushingFromCompose.value = false
        }
        val decoSet = buildDiffDecorations(view, lineDiffs, isDark)
        val effect = diffCompartment.reconfigure(
            EditorView.decorations.of(decoSet)
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }
    LaunchedEffect(isStreaming, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val effect = editableCompartment.reconfigure(
            EditorView.editable.of(!isStreaming)
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }

    LaunchedEffect(isDark, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val effect = themeCompartment.reconfigure(
            if (isDark) materialDark else materialLight
        )
        val tr = js("({})")
        tr.effects = arrayOf(effect)
        view.dispatch(tr)
    }


    LaunchedEffect(jumpToOffset, editorRef.value) {
        val view = editorRef.value ?: return@LaunchedEffect
        val off = jumpToOffset ?: return@LaunchedEffect
        if (off >= 0) cmJumpToOffset(view, off)
    }

    LaunchedEffect(lineDiffs, errorLine, editorRef.value, markerLayerRef.value, isDark) {
        val view = editorRef.value ?: return@LaunchedEffect
        val layer = markerLayerRef.value ?: return@LaunchedEffect
        updateScrollMarkers(view, layer, lineDiffs, errorLine, isDark)
    }
}

private fun buildDiffDecorations(
    view: EditorView,
    lineDiffs: List<LineDiff>,
    isDark: Boolean
): dynamic {
    if (lineDiffs.isEmpty()) return Decoration.none

    val docLength = view.state.doc.length as Int
    if (docLength == 0) return Decoration.none

    val maxLine = view.state.doc.lines as Int
    val ranges = js("([])")
    lineDiffs.sortedBy { it.line }.forEach { diff ->
        if (diff.line <= 0 || diff.line > maxLine) return@forEach
        val line = view.state.doc.line(diff.line)
        when (diff.kind) {
            LineDiffKind.Added, LineDiffKind.Removed -> {
                val isAdded = diff.kind == LineDiffKind.Added
                val className = if (isAdded) {
                    "cm-diff-added"
                } else {
                    "cm-diff-removed"
                }
                val lightColor = if (isAdded) "46, 125, 50" else "198, 40, 40"
                val darkColor = if (isAdded) "102, 187, 106" else "239, 83, 80"
                val style = if (isDark) {
                    "background-color: rgba($darkColor, 0.28); box-shadow: inset 3px 0 0 rgba($darkColor, 0.7);"
                } else {
                    "background-color: rgba($lightColor, 0.16); box-shadow: inset 3px 0 0 rgba($lightColor, 0.7);"
                }
                val spec = js("({})")
                spec["class"] = className
                val attrs = js("({})")
                attrs["style"] = style
                spec["attributes"] = attrs
                val deco = Decoration.line(spec)
                ranges.push(deco.range(line.from as Int))
            }
            LineDiffKind.Changed -> {
                val offsetStart = diff.offsetStart
                val offsetEnd = diff.offsetEnd
                val (safeFrom, safeTo) = if (offsetStart != null && offsetEnd != null) {
                    val from = offsetStart.coerceIn(0, docLength)
                    val to = offsetEnd.coerceIn(0, docLength)
                    if (from >= to) return@forEach
                    from to to
                } else {
                    val start = diff.columnStart
                    val end = diff.columnEnd
                    if (start == null || end == null) return@forEach
                    val from = (line.from as Int) + start
                    val to = (line.from as Int) + end
                    val lineFrom = line.from as Int
                    val lineTo = line.to as Int
                    val safeFrom = from.coerceAtLeast(lineFrom).coerceAtMost(lineTo)
                    val safeTo = to.coerceAtLeast(lineFrom).coerceAtMost(lineTo)
                    if (safeFrom >= safeTo) return@forEach
                    safeFrom to safeTo
                }

                val style = if (isDark) {
                    "background-color: rgba(255, 193, 7, 0.32); border-radius: 3px;"
                } else {
                    "background-color: rgba(255, 160, 0, 0.22); border-radius: 3px;"
                }
                val spec = js("({})")
                spec["class"] = "cm-diff-changed-value"
                val attrs = js("({})")
                attrs["style"] = style
                spec["attributes"] = attrs
                val deco = Decoration.mark(spec)
                ranges.push(deco.range(safeFrom, safeTo))
            }
        }
    }
    return Decoration.set(ranges, true)
}

private enum class ScrollMarkerKind(val priority: Int) {
    Changed(1),
    Added(2),
    Removed(2),
    Error(3)
}

private fun updateScrollMarkers(
    view: EditorView,
    layer: HTMLElement,
    lineDiffs: List<LineDiff>,
    errorLine: Int?,
    isDark: Boolean
) {
    layer.innerHTML = ""
    val totalLines = view.state.doc.lines as Int
    if (totalLines <= 0) return

    val byLine = mutableMapOf<Int, ScrollMarkerKind>()
    fun assign(line: Int, kind: ScrollMarkerKind) {
        val safeLine = line.coerceIn(1, totalLines)
        val existing = byLine[safeLine]
        if (existing == null || kind.priority > existing.priority) {
            byLine[safeLine] = kind
        }
    }

    lineDiffs.forEach { diff ->
        val kind = when (diff.kind) {
            LineDiffKind.Added -> ScrollMarkerKind.Added
            LineDiffKind.Removed -> ScrollMarkerKind.Removed
            LineDiffKind.Changed -> ScrollMarkerKind.Changed
        }
        assign(diff.line, kind)
    }
    if (errorLine != null) {
        assign(errorLine, ScrollMarkerKind.Error)
    }

    if (byLine.isEmpty()) return

    val denom = max(1, totalLines - 1)
    val errorColor = if (isDark) "rgba(255, 82, 82, 0.9)" else "rgba(211, 47, 47, 0.85)"
    val addedColor = if (isDark) "rgba(102, 187, 106, 0.75)" else "rgba(46, 125, 50, 0.7)"
    val removedColor = if (isDark) "rgba(239, 83, 80, 0.75)" else "rgba(198, 40, 40, 0.7)"
    val changedColor = if (isDark) "rgba(255, 193, 7, 0.85)" else "rgba(255, 160, 0, 0.8)"

    byLine.forEach { (line, kind) ->
        val index = (line - 1).coerceIn(0, totalLines - 1)
        val pct = (index.toDouble() / denom.toDouble()) * 100.0
        val marker = document.createElement("div") as HTMLDivElement
        marker.style.position = "absolute"
        marker.style.left = "1px"
        marker.style.right = "1px"
        marker.style.height = if (kind == ScrollMarkerKind.Error) "4px" else "3px"
        marker.style.borderRadius = "2px"
        marker.style.top = "${pct}%"
        marker.style.transform = "translateY(-50%)"
        marker.style.background = when (kind) {
            ScrollMarkerKind.Error -> errorColor
            ScrollMarkerKind.Added -> addedColor
            ScrollMarkerKind.Removed -> removedColor
            ScrollMarkerKind.Changed -> changedColor
        }
        layer.appendChild(marker)
    }
}

